import { ReactNode } from 'react';

// Shared presentation for the research pages. A backtest table is mostly the same three
// decisions repeated — sign colouring, a verdict pill, and "this number may be absent" —
// so they live here rather than being re-typed per page.

export function formatPercent(value: number | null | undefined, digits = 2): string {
    if (value === null || value === undefined) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}%`;
}

export function formatNumber(value: number | null | undefined, digits = 2): string {
    if (value === null || value === undefined) return '—';
    return `${value >= 0 ? '+' : ''}${value.toFixed(digits)}`;
}

export function formatDate(value: string | null | undefined): string {
    if (!value) return '—';
    return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

export function signColor(value: number | null | undefined): string {
    if (value === null || value === undefined) return 'var(--foreground-muted)';
    if (value > 0) return 'var(--success)';
    if (value < 0) return 'var(--danger)';
    return 'var(--foreground-muted)';
}

// `screen` carries no bar — its window list is whatever was passed on the command line — so
// the API sends `passed: null` there and it must not render as a failure.
export function Verdict({ passed }: { passed: boolean | null }) {
    if (passed === null) {
        return (
            <span
                className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
                style={{ background: 'var(--background-subtle)', color: 'var(--foreground-muted)' }}
                title="This sample carries no pass/fail bar"
            >
                no bar
            </span>
        );
    }
    return (
        <span
            className="inline-flex rounded-full px-2.5 py-1 text-xs font-semibold"
            style={
                passed
                    ? { background: 'var(--success-tint)', color: 'var(--success)' }
                    : { background: 'var(--danger-tint)', color: 'var(--danger)' }
            }
        >
            {passed ? 'PASS' : 'FAIL'}
        </span>
    );
}

export function Stat({ label, value, color, hint }: { label: string; value: ReactNode; color?: string; hint?: string }) {
    return (
        <div className="min-w-0">
            <div className="text-xs font-medium uppercase tracking-wide" style={{ color: 'var(--foreground-muted)' }}>
                {label}
            </div>
            <div className="mt-1 truncate text-xl font-semibold tabular-nums" style={color ? { color } : undefined} title={hint}>
                {value}
            </div>
        </div>
    );
}

export function Th({ children, align = 'left' }: { children: ReactNode; align?: 'left' | 'right' }) {
    return (
        <th
            className={`border-b py-2 pr-4 font-medium ${align === 'right' ? 'text-right' : 'text-left'}`}
            style={{ borderColor: 'var(--card-border)' }}
        >
            {children}
        </th>
    );
}

export function Td({ children, align = 'left', color, mono }: { children: ReactNode; align?: 'left' | 'right'; color?: string; mono?: boolean }) {
    return (
        <td
            className={`border-b py-3 pr-4 ${align === 'right' ? 'text-right' : 'text-left'} ${mono ? 'tabular-nums' : ''}`}
            style={{ borderColor: 'var(--card-border)', ...(color ? { color } : {}) }}
        >
            {children}
        </td>
    );
}

export function ErrorCard({ message, onRetry }: { message: string; onRetry: () => void }) {
    return (
        <section className="surface-card rounded-[28px] px-5 py-5 sm:px-6" role="alert">
            <p className="text-sm font-medium" style={{ color: 'var(--danger)' }}>
                {message}
            </p>
            <button type="button" onClick={onRetry} className="button-secondary mt-4 rounded-full px-4 py-2 text-sm font-medium">
                Retry
            </button>
        </section>
    );
}
